/**
 * Cluster generation pin — admin tool for the Explore view.
 *
 * The analytics planner pins every ILO cluster read to the tenant's
 * latest *completed* clustering generation (the scheduled path runs
 * bisecting, or flat kmeans for low-data tenants). Pinning a specific
 * generation here — e.g. an older run — makes Explore, kernels, and permalinks
 * all read clusters from that generation instead.
 *
 * Stored in the URL as the `gen` query param (e.g.
 * `/?scope=…&range=1M&gen=<uuid>`), NOT localStorage. Three reasons:
 *   - shareable: a copied URL reproduces the same pinned cluster set;
 *   - available synchronously at load (no tenant lookup), so the pin
 *     applies to the very first queries — this is why `runQuery` no
 *     longer awaits /v1/me before reading the pin;
 *   - per-view, not a sticky hidden per-browser setting.
 *
 * The GenerationPicker writes the param (via react-router so the tree
 * re-renders); App watches it and fires `onGenerationPinChange` so the
 * caches that can't see the URL (clusterTree, cluster labels,
 * dataClient's result cache) invalidate on a change. `runQuery` reads
 * the value via `getPinnedGenerationId` and expresses it as a
 * `generation_id` stepFilter on each ILO step (O-1547, PR #1197).
 *
 * Only the generation UUID lives in the URL — algorithm/date metadata
 * for display is resolved from the generation list the picker fetches.
 */
import { readUrlParam } from "./urlParams.js";

export const GEN_PARAM = "gen";

// Hot-path read for runQuery + the cache subscribers. Synchronous, no
// tenant, no storage. Null when unpinned.
export function getPinnedGenerationId() {
    try {
        return readUrlParam(GEN_PARAM) || null;
    } catch {
        return null;
    }
}

// Whether the GenerationPicker should render. It is an admin/debug tool, so it
// is offered to tenant admins and in local dev (no auth means no roles). A set
// pin remains visible to any reader so it can always be seen and cleared.
export function generationPickerVisible({ isTenantAdmin, authRequired, pinned }) {
    if (pinned) return true;
    return isTenantAdmin || !authRequired;
}

// Whether the "New clustering pass" launch form should render. Launching is
// a write into the clustering pipeline, so it's offered only to the same
// admin / local-dev surface that may manage generations. Unlike
// generationPickerVisible, a set pin does NOT unlock it:
// a pin makes the (read-only) picker visible to anyone who holds the link, but
// must never expose the write control. Pure — the caller resolves the flags.
export function generationLaunchVisible({ isTenantAdmin, authRequired }) {
    return isTenantAdmin || !authRequired;
}

// ── Change channel ─────────────────────────────────────────────────
//
// Fired when the pin changes or the unpinned picker opens to refresh latest.
// Cache modules (dataClient, clusterTree, dimensionDefs)
// subscribe at import and invalidate their own state. Subscriber
// failures are isolated so one bad cache can't block the others.
const _subscribers = new Set();
let generationRevision = 0;

export function getGenerationRevision() {
    return generationRevision;
}

export function onGenerationPinChange(cb) {
    _subscribers.add(cb);
    return () => _subscribers.delete(cb);
}

export function notifyGenerationPinChange() {
    generationRevision += 1;
    for (const cb of _subscribers) {
        try { cb(); } catch (err) { console.warn("[generationPin] subscriber threw:", err); }
    }
}
